/*
  Espressoshop: flygande kaffebönor i hero (sections/es-hero-bonor.liquid).

  Fysiken är porterad från GetLayers-mallen "Brewns" (public domain, bean-field.ts):
  viktlösa bönor som driver i en långsam luftström, knuffas undan av pekaren,
  studsar mot varandra med friktion (så att snedträffar ger snurr) och virvlar
  runt bildens mitt när man scrollar. Bönan är modellerad här i koden, så ingen
  3D-fil behövs. Ritningen pausar när sektionen inte syns eller fliken är dold.
  Med "minska rörelse" står bönorna still.
*/
import {
  Color, DirectionalLight, Fog, HemisphereLight, InstancedMesh, Matrix4, MeshStandardMaterial,
  NeutralToneMapping, PerspectiveCamera, Quaternion, Scene, SphereGeometry, SRGBColorSpace, Vector3, WebGLRenderer,
} from 'https://cdn.jsdelivr.net/npm/three@0.170.0/build/three.module.min.js';

const FIELD = {
  step: 1 / 120, maxSteps: 8, wander: 0.16, wanderRate: { min: 0.12, max: 0.34 },
  drag: 0.42, spinDrag: 0.3, spinWander: 0.22, wall: 4.5, bleed: 0.6,
  restitution: 0.62, friction: 0.35, pointerReach: 0.16, pointerPush: 26, pointerWake: 1.8,
  pointerSpin: 3.2, maxSpeed: 7, maxSpin: 6, radius: 0.36, vortexCore: 3, vortexInward: 0.22, vortexSpin: 0.35,
};
const VIEW = { fov: 30, distance: 20 };
const DEPTH = { near: 2, far: -10 };
const BEAN_LENGTH = 0.075;
const SCALE = { min: 0.6, max: 1.8 };
const VORTEX = { strength: 2.4, fullSpeed: 1600, follow: 3 };
const POINTER_SETTLE = 8;
const POINTER_MAX_SPEED = 24;

const lerp = (a, b, t) => a + (b - a) * t;

/* ── Bönan: en tillplattad ellipsoid med en S-formad fåra på den platta sidan ── */
function beanGeometry() {
  const g = new SphereGeometry(0.5, 56, 40);
  const p = g.attributes.position;
  const v = new Vector3();
  for (let i = 0; i < p.count; i++) {
    v.fromBufferAttribute(p, i);
    // Längd längs z, bredd x, tjocklek y.
    let x = v.x * 0.68, y = v.y * 0.5, z = v.z;
    if (y > 0) {
      y *= 0.62; // den platta sidan
      const centre = 0.05 * Math.sin(z * 5.2);
      const d = (x - centre) / 0.045;
      const edge = 1 - Math.min(1, Math.abs(z) / 0.5) ** 4;
      y -= 0.13 * Math.exp(-d * d) * edge;
    }
    p.setXYZ(i, x, y, z);
  }
  g.computeVertexNormals();
  return g;
}

function randomUnit(t) {
  const z = Math.random() * 2 - 1, a = Math.random() * Math.PI * 2, r = Math.sqrt(1 - z * z);
  return t.set(r * Math.cos(a), r * Math.sin(a), z);
}
function randomRotation(t) {
  const u1 = Math.random(), u2 = Math.random() * Math.PI * 2, u3 = Math.random() * Math.PI * 2;
  const a = Math.sqrt(1 - u1), b = Math.sqrt(u1);
  return t.set(a * Math.sin(u2), a * Math.cos(u2), b * Math.sin(u3), b * Math.cos(u3));
}

const _d = new Vector3(), _n = new Vector3(), _t = new Vector3(), _p = new Vector3();
const _w = new Vector3(), _a = new Vector3(), _ri = new Vector3(), _rj = new Vector3(), _rel = new Vector3();
const _q = new Quaternion();
const halfHeight = (view, z) => (view.distance - z) * view.tanHalf;

function createField(count, base, view) {
  const beans = [];
  for (let i = 0; i < count; i++) {
    const scale = SCALE.min * Math.pow(SCALE.max / SCALE.min, Math.random());
    const size = base * scale, radius = size * FIELD.radius, mass = scale ** 3;
    const position = new Vector3();
    for (let attempt = 0; attempt < 40; attempt++) {
      const z = lerp(view.far, view.near, Math.random());
      const half = halfHeight(view, z) * 0.9;
      position.set((Math.random() * 2 - 1) * half * view.aspect, (Math.random() * 2 - 1) * half, z);
      if (beans.every((o) => o.position.distanceTo(position) > o.radius + radius)) break;
    }
    beans.push({
      position, velocity: randomUnit(new Vector3()).multiplyScalar(lerp(0.08, 0.25, Math.random())),
      rotation: randomRotation(new Quaternion()), spin: randomUnit(new Vector3()).multiplyScalar(lerp(0.15, 0.5, Math.random())),
      size, radius, mass, inertia: 0.4 * mass * radius * radius,
      phase: [Math.random() * 7, Math.random() * 7, Math.random() * 7],
      rate: [0, 0, 0].map(() => lerp(FIELD.wanderRate.min, FIELD.wanderRate.max, Math.random())),
    });
  }
  return { beans, debt: 0, time: 0 };
}

function moveBean(bean, h, time, world) {
  const { position: p, velocity: v, spin: w, phase, rate } = bean;
  const { view, pointer, vortex } = world;
  const inverseMass = 1 / bean.mass;
  _a.set(Math.sin(time * rate[0] + phase[0]), Math.sin(time * rate[1] + phase[1]), 0.6 * Math.sin(time * rate[2] + phase[2])).multiplyScalar(FIELD.wander);

  const halfH = halfHeight(view, p.z), inset = bean.radius * (1 - FIELD.bleed);
  const limitX = halfH * view.aspect - inset, limitY = halfH - inset;
  if (Math.abs(p.x) > limitX) _a.x -= Math.sign(p.x) * (Math.abs(p.x) - limitX) * FIELD.wall;
  if (Math.abs(p.y) > limitY) _a.y -= Math.sign(p.y) * (Math.abs(p.y) - limitY) * FIELD.wall;
  if (p.z > view.near) _a.z -= (p.z - view.near) * FIELD.wall;
  if (p.z < view.far) _a.z += (view.far - p.z) * FIELD.wall;

  if (pointer.active) {
    const reach = FIELD.pointerReach * 2 * view.distance * view.tanHalf;
    _d.subVectors(p, pointer.origin);
    const along = _d.dot(pointer.direction);
    _d.addScaledVector(pointer.direction, -along);
    const distance = _d.length();
    if (along > 0 && distance < reach) {
      const falloff = 1 - distance / reach;
      if (distance > 1e-5) {
        _n.copy(_d).divideScalar(distance);
        _a.addScaledVector(_n, FIELD.pointerPush * falloff * falloff * inverseMass);
        _w.crossVectors(_n, pointer.velocity).multiplyScalar(-FIELD.pointerSpin * falloff * inverseMass * h);
        w.add(_w);
      }
      _a.addScaledVector(pointer.velocity, FIELD.pointerWake * falloff * inverseMass);
    }
  }

  if (vortex.strength) {
    const dx = p.x - vortex.centre.x, dy = p.y - vortex.centre.y, r = Math.hypot(dx, dy);
    if (r > 1e-4) {
      const swirl = vortex.strength * Math.min(1, r / FIELD.vortexCore);
      _a.x += (-dy / r) * swirl - (dx / r) * Math.abs(swirl) * FIELD.vortexInward;
      _a.y += (dx / r) * swirl - (dy / r) * Math.abs(swirl) * FIELD.vortexInward;
      w.z += swirl * FIELD.vortexSpin * h;
    }
  }

  v.addScaledVector(_a, h).multiplyScalar(Math.exp(-FIELD.drag * h));
  if (v.lengthSq() > FIELD.maxSpeed ** 2) v.setLength(FIELD.maxSpeed);
  p.addScaledVector(v, h);

  w.x += Math.sin(time * rate[1] * 0.7 + phase[2]) * FIELD.spinWander * h;
  w.y += Math.sin(time * rate[2] * 0.7 + phase[0]) * FIELD.spinWander * h;
  w.z += Math.sin(time * rate[0] * 0.7 + phase[1]) * FIELD.spinWander * h;
  w.multiplyScalar(Math.exp(-FIELD.spinDrag * h));
  if (w.lengthSq() > FIELD.maxSpin ** 2) w.setLength(FIELD.maxSpin);
  const angle = w.length() * h;
  if (angle > 1e-9) { _q.setFromAxisAngle(_w.copy(w).normalize(), angle); bean.rotation.premultiply(_q).normalize(); }
}

function collide(i, j) {
  _d.subVectors(j.position, i.position);
  const reach = i.radius + j.radius, dsq = _d.lengthSq();
  if (dsq >= reach * reach || dsq < 1e-12) return;
  const distance = Math.sqrt(dsq);
  _n.copy(_d).divideScalar(distance);
  const im = 1 / i.mass, jm = 1 / j.mass;
  const correction = (reach - distance) / (im + jm);
  i.position.addScaledVector(_n, -correction * im);
  j.position.addScaledVector(_n, correction * jm);
  _ri.copy(_n).multiplyScalar(i.radius);
  _rj.copy(_n).multiplyScalar(-j.radius);
  _rel.copy(j.velocity).add(_t.crossVectors(j.spin, _rj)).sub(i.velocity).sub(_p.crossVectors(i.spin, _ri));
  const closing = _rel.dot(_n);
  if (closing >= 0) return;
  const normal = (-(1 + FIELD.restitution) * closing) / (im + jm);
  _t.copy(_rel).addScaledVector(_n, -closing);
  const sliding = _t.length();
  let tangent = 0;
  if (sliding > 1e-6) {
    _t.divideScalar(sliding);
    tangent = Math.min(FIELD.friction * normal, sliding / (im + jm + (i.radius ** 2) / i.inertia + (j.radius ** 2) / j.inertia));
  }
  _p.copy(_n).multiplyScalar(normal).addScaledVector(_t, -tangent);
  i.velocity.addScaledVector(_p, -im);
  j.velocity.addScaledVector(_p, jm);
  i.spin.add(_w.crossVectors(_ri, _p).multiplyScalar(-1 / i.inertia));
  j.spin.add(_w.crossVectors(_rj, _p).multiplyScalar(1 / j.inertia));
}

function stepField(field, dt, world) {
  field.debt = Math.min(field.debt + dt, FIELD.step * FIELD.maxSteps);
  const { beans } = field;
  while (field.debt >= FIELD.step) {
    for (const b of beans) moveBean(b, FIELD.step, field.time, world);
    for (let i = 0; i < beans.length; i++) for (let j = i + 1; j < beans.length; j++) collide(beans[i], beans[j]);
    field.debt -= FIELD.step;
    field.time += FIELD.step;
  }
}

function init(section) {
  const mount = section.querySelector('[data-es-bonor-canvas]');
  if (!mount || mount.dataset.ready) return;
  mount.dataset.ready = '1';

  const mobile = window.innerWidth < 768 || matchMedia('(hover: none) and (pointer: coarse)').matches;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const count = Number(section.dataset[mobile ? 'mobile' : 'desktop']) || (mobile ? 16 : 34);

  let renderer;
  try {
    renderer = new WebGLRenderer({ alpha: true, antialias: true, stencil: false, powerPreference: mobile ? 'default' : 'high-performance' });
  } catch { return; }
  renderer.setClearAlpha(0);
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.toneMapping = NeutralToneMapping;
  renderer.toneMappingExposure = 1.1;
  mount.appendChild(renderer.domElement);

  const scene = new Scene();
  scene.fog = new Fog(new Color(section.dataset.bg || '#140c09'), VIEW.distance + 2, VIEW.distance + 16);
  const camera = new PerspectiveCamera(VIEW.fov, 1, 1, 60);
  camera.position.set(0, 0, VIEW.distance);
  camera.lookAt(0, 0, 0);

  scene.add(new HemisphereLight(0xffe7cf, 0x1a0f0a, 0.9));
  const key = new DirectionalLight(0xfff1e0, 2.6); key.position.set(-5, 6, 7); scene.add(key);
  const rim = new DirectionalLight(0xffc98f, 1.8); rim.position.set(5, 3, -6); scene.add(rim);

  const material = new MeshStandardMaterial({ color: 0xffffff, roughness: 0.42, metalness: 0.05 });
  const beans = new InstancedMesh(beanGeometry(), material, count);
  beans.frustumCulled = false;
  const shades = ['#3b2014', '#4a2818', '#56301c', '#2f1a10', '#633822'];
  for (let i = 0; i < count; i++) beans.setColorAt(i, new Color(shades[i % shades.length]));
  scene.add(beans);

  const tanHalf = Math.tan((VIEW.fov * Math.PI) / 360);
  const view = { distance: VIEW.distance, tanHalf, aspect: 1, near: DEPTH.near, far: DEPTH.far };
  const world = {
    view,
    pointer: { active: false, origin: new Vector3(), direction: new Vector3(), velocity: new Vector3() },
    vortex: { centre: new Vector3(0, 0, (DEPTH.near + DEPTH.far) / 2), strength: 0 },
  };

  let field = null;
  const fit = () => {
    const w = mount.clientWidth, h = mount.clientHeight;
    if (!w || !h) return;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    view.aspect = camera.aspect;
    renderer.setPixelRatio(Math.min(devicePixelRatio, mobile ? 2 : 1.5));
    renderer.setSize(w, h, false);
    if (!field) field = createField(count, BEAN_LENGTH * 2 * VIEW.distance * tanHalf, view);
    dirty = true;
  };

  const matrix = new Matrix4(), scale = new Vector3();
  const write = () => {
    field.beans.forEach((b, i) => { matrix.compose(b.position, b.rotation, scale.setScalar(b.size)); beans.setMatrixAt(i, matrix); });
    beans.instanceMatrix.needsUpdate = true;
  };

  let dirty = true, onScreen = false, last = 0, lastDraw = 0, lastScroll = null;
  const aim = { x: 0, y: 0 };
  const ray = new Vector3();
  const frame = (time) => {
    requestAnimationFrame(frame);
    if (!field || !onScreen || document.hidden) { last = 0; lastScroll = null; return; }
    const dt = last ? Math.min(0.05, (time - last) / 1000) : 0;
    last = time; lastDraw = time;
    if (!reduced && dt > 0) {
      const { pointer } = world;
      if (pointer.active) {
        ray.set(aim.x, aim.y, 0.5).unproject(camera).sub(camera.position).normalize();
        pointer.origin.copy(camera.position);
        pointer.direction.copy(ray);
      }
      pointer.velocity.multiplyScalar(Math.exp(-POINTER_SETTLE * dt));
      const scrollNow = window.scrollY;
      const speed = lastScroll === null ? 0 : (scrollNow - lastScroll) / dt;
      lastScroll = scrollNow;
      const swirl = Math.max(-1, Math.min(1, speed / VORTEX.fullSpeed)) * VORTEX.strength;
      world.vortex.strength += (swirl - world.vortex.strength) * (1 - Math.exp(-VORTEX.follow * dt));
      stepField(field, dt, world);
      dirty = true;
    }
    if (!dirty) return;
    write();
    renderer.render(scene, camera);
    dirty = false;
    section.classList.add('is-bonor-klar');
  };

  new ResizeObserver(() => requestAnimationFrame(fit)).observe(mount);
  new IntersectionObserver(([e]) => { onScreen = e.isIntersecting; dirty = true; }, { rootMargin: '10% 0px' }).observe(mount);
  document.addEventListener('visibilitychange', () => { dirty = true; });

  let lastMove = { x: 0, y: 0, t: 0 };
  const scratch = new Vector3();
  const onMove = (e) => {
    const box = mount.getBoundingClientRect();
    aim.x = ((e.clientX - box.left) / box.width) * 2 - 1;
    aim.y = -(((e.clientY - box.top) / box.height) * 2 - 1);
    const elapsed = (e.timeStamp - lastMove.t) / 1000;
    if (world.pointer.active && elapsed > 0 && elapsed < 0.1) {
      const unit = (2 * VIEW.distance * tanHalf) / box.height;
      scratch.set(((e.clientX - lastMove.x) / elapsed) * unit, -((e.clientY - lastMove.y) / elapsed) * unit, 0);
      world.pointer.velocity.lerp(scratch, 0.5).clampLength(0, POINTER_MAX_SPEED);
    }
    lastMove = { x: e.clientX, y: e.clientY, t: e.timeStamp };
    world.pointer.active = true;
  };
  const onLeave = () => { world.pointer.active = false; };
  section.addEventListener('pointermove', onMove, { passive: true });
  section.addEventListener('pointerdown', onMove, { passive: true });
  section.addEventListener('pointerleave', onLeave, { passive: true });

  fit();
  // Första bilden direkt, så att bönorna syns redan innan animationen startar
  // (och står kvar som stillbild med "minska rörelse").
  if (field) { write(); renderer.render(scene, camera); section.classList.add('is-bonor-klar'); }
  requestAnimationFrame(frame);
}

document.querySelectorAll('[data-es-hero-bonor]').forEach(init);
document.addEventListener('shopify:section:load', (e) => {
  const s = e.target.querySelector('[data-es-hero-bonor]');
  if (s) init(s);
});
